import { TFunction, useTranslation } from 'react-i18next';
import { QuickReply } from '@connect-x-app/shared-domain-models';
import { FormValidator, ValidatorResponse } from '@connect-x-app/shared-utils';
import { FormValidationErrors, useForm } from '@connect-x-app/web-state';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { InputField } from '@connect-x-app/web-ui';

import { ModalForm } from '../../roles/components';
import { IndexableQuickReply } from '../models/indexableQuickReply';

type Props = {
  closeModal: () => void;
  isLoading: boolean;
  onSubmit: (values: IndexableQuickReply) => void;
  quickReplies: QuickReply[];
  quickReply?: IndexableQuickReply;
};

const initialForm: QuickReply = {
  description: '',
  order: '',
  text: '',
};

const validateOrder = (validator: FormValidator, order: string): ValidatorResponse | undefined => {
  const requiredValidation = validator.required(order);
  const numberValidation = validator.number(order);
  const integerValidation = validator.isInteger(order);
  const minValidation = validator.minValue(+order, 0);
  const maxValidation = validator.maxValue(+order, 999);

  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  if (!numberValidation.isValid) {
    return numberValidation;
  }

  if (!integerValidation.isValid) {
    return integerValidation;
  }

  if (!minValidation.isValid) {
    return minValidation;
  }

  if (!maxValidation.isValid) {
    return maxValidation;
  }

  return undefined;
};

const validateText = (
  validator: FormValidator,
  text: string,
  existingQuickReplies: QuickReply[],
  t: TFunction,
  quickReply?: QuickReply,
): ValidatorResponse | undefined => {
  const requiredValidation = validator.required(text);
  const maxLengthValidation = validator.maxLength(text, 100);

  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  const filteredExistingQuickReplies = existingQuickReplies.filter(e => e.text !== quickReply?.text);

  if (filteredExistingQuickReplies.some(r => r.text.toUpperCase().trim() === text.toUpperCase().trim())) {
    return {
      isValid: false,
      message: t('QUICK_REPLIES.FORM.ERROR.TEXT_DUPLICATE'),
    };
  }

  return undefined;
};

const validateDescription = (validator: FormValidator, description: string): ValidatorResponse | undefined => {
  const maxLengthValidation = validator.maxLength(description, 100);
  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  return undefined;
};

const validateForm = (
  formValue: QuickReply,
  existingQuickReplies: QuickReply[],
  t: TFunction,
  quickReply?: QuickReply,
): FormValidationErrors<QuickReply> => {
  const validator = new FormValidator();

  return {
    description: validateDescription(validator, formValue?.description ?? ''),
    order: validateOrder(validator, formValue.order?.toString() ?? ''),
    text: validateText(validator, formValue.text, existingQuickReplies, t, quickReply),
  };
};

export const QuickReplyModal = ({ closeModal, isLoading, onSubmit, quickReplies, quickReply }: Props) => {
  const { t } = useTranslation();

  const form = useForm<IndexableQuickReply>({
    initialForm: quickReply ?? (initialForm as IndexableQuickReply),
    submitForm: (quickReply: IndexableQuickReply) =>
      onSubmit({
        ...quickReply,
        order: +quickReply.order,
      }),
    validateForm: values => validateForm(values, quickReplies, t, quickReply),
  });

  return (
    <ModalForm
      disabled={!form.isValid || !form.isDirty}
      headerTitle={quickReply ? t('QUICK_REPLIES.ACTIONS.UPDATE') : t('QUICK_REPLIES.ACTIONS.ADD')}
      isDirty={form.isDirty}
      isSubmitting={isLoading}
      onCancel={closeModal}
      onSubmit={form.submit}
      submitButtonText={quickReply ? t('SHARED.BUTTONS.SAVE') : t('SHARED.BUTTONS.ADD')}
      testId={ADMIN_TEST_IDS.quickReply.modal}
    >
      <InputField
        label={t('QUICK_REPLIES.FORM.TEXT')}
        name="text"
        onChange={form.setAttribute}
        required
        showInlineBottomError
        validation={form.validationErrors?.text}
        value={form.values?.text}
      />
      <InputField
        label={t('QUICK_REPLIES.FORM.DESCRIPTION')}
        name="description"
        onChange={form.setAttribute}
        showInlineBottomError
        validation={form.validationErrors?.description}
        value={form.values?.description}
      />
      <InputField
        label={t('QUICK_REPLIES.FORM.ORDER')}
        name="order"
        onChange={form.setAttribute}
        required
        showInlineBottomError
        validation={form.validationErrors?.order}
        value={form.values?.order?.toString() ?? ''}
      />
    </ModalForm>
  );
};
